/**
 * Navigation Component (Refactored)
 * 
 * Cleaner version using extracted subcomponents:
 * - ThemeDropdown for theme controls
 * - Maintains all functionality from original Navigation.js
 */

import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaTimes, FaPalette, FaChevronDown } from 'react-icons/fa';
import { navItems } from '../data/navigation';
import { useTheme } from '../contexts/ThemeContext';
import { useColorScheme } from '../contexts/ColorSchemeContext';
import BatteryBar from './BatteryBar';
import ThemeDropdown from './navigation/ThemeDropdown';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isMobileThemeOpen, setIsMobileThemeOpen] = useState(false);
  const [customColor, setCustomColorValue] = useState('#D4AF37');
  const themeDropdownRef = useRef<HTMLDivElement>(null);
  
  const { theme, mode, colorScheme, setTheme, setMode, setColorScheme, clearTextColorOverrides } = useTheme();
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const handleColorSchemeChange = (scheme: string) => {
    setColorScheme(scheme as any);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = navItems.map(item => document.getElementById(item.id)).filter(Boolean);
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (themeDropdownRef.current && !themeDropdownRef.current.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/90 dark:bg-black/90 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center h-16">
          {/* Centered Navigation - All items evenly spaced */}
          <div className="hidden lg:flex items-center justify-center space-x-6 max-w-6xl">
            {/* BatteryBar as first nav item */}
            <button
              onClick={() => scrollToSection('hero')}
              className="hover:scale-105 transition-transform duration-300 px-3 py-2"
            >
              <BatteryBar />
            </button>

            {/* All other nav items */}
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                    activeSection === item.id
                      ? `${colors.bg} ${colors.textPrimary} font-semibold`
                      : `${colors.textSecondary} hover:${colors.cardBackground} hover:${colors.text} hover:scale-105`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Theme Toggle as last nav item */}
            <div className="relative" ref={themeDropdownRef}>
              <button
                onClick={() => setIsThemeOpen(!isThemeOpen)}
                className={`p-2 rounded-full transition-all duration-300 ${colors.cardBackground} hover:opacity-80 ${colors.textPrimary}`}
                aria-label="Theme settings"
              >
                <div className="flex items-center space-x-1">
                  <FaPalette className="w-4 h-4" />
                  <FaChevronDown className={`w-3 h-3 transition-transform duration-200 ${isThemeOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              <ThemeDropdown
                isOpen={isThemeOpen}
                customColor={customColor}
                setCustomColorValue={setCustomColorValue}
                onColorSchemeChange={handleColorSchemeChange}
              />
            </div>
          </div>

          {/* Mobile: BatteryBar + Menu Button */}
          <div className="lg:hidden flex items-center justify-between w-full">
            <button
              onClick={() => scrollToSection('hero')}
              className="hover:scale-105 transition-transform duration-300"
            >
              <BatteryBar />
            </button>
            <button
              className="p-2 rounded-lg transition-colors duration-300 theme-text-title"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`md:hidden fixed top-0 right-0 h-full w-64 theme-card-bg shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 50 }}
      >
        <div className="flex flex-col h-full p-6">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold theme-text-title">Menu</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <FaTimes className="w-5 h-5 theme-text-title" />
            </button>
          </div>

          <nav className="flex-1 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    activeSection === item.id
                      ? `${colors.bg} text-white`
                      : `theme-text-body hover:${colors.cardBackground}`
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Mobile Theme Controls - Full Featured */}
          <div className="pt-4 border-t theme-border">
            <button
              onClick={() => setIsMobileThemeOpen(!isMobileThemeOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg theme-text-body hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <span className="flex items-center space-x-2">
                <FaPalette className="w-4 h-4" />
                <span>Theme</span>
              </span>
              <FaChevronDown className={`w-3 h-3 transition-transform ${isMobileThemeOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isMobileThemeOpen && (
              <div className="mt-2 px-4 space-y-4 max-h-96 overflow-y-auto">
                {/* Theme Type */}
                <div>
                  <p className="text-xs theme-text-muted mb-2">Theme Type</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setTheme('default'); }}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        theme === 'default'
                          ? 'bg-gray-300 dark:bg-gray-600 theme-text-title'
                          : 'bg-gray-100 dark:bg-gray-800 theme-text-body'
                      }`}
                    >
                      Default
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTheme('sepia'); }}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        theme === 'sepia'
                          ? 'bg-gray-300 dark:bg-gray-600 theme-text-title'
                          : 'bg-gray-100 dark:bg-gray-800 theme-text-body'
                      }`}
                    >
                      Sepia
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTheme('dracula'); }}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        theme === 'dracula'
                          ? 'bg-gray-300 dark:bg-gray-600 theme-text-title'
                          : 'bg-gray-100 dark:bg-gray-800 theme-text-body'
                      }`}
                    >
                      Dracula
                    </button>
                  </div>
                </div>

                {/* Mode Toggle */}
                <div>
                  <p className="text-xs theme-text-muted mb-2">Mode</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setMode('light'); }}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        mode === 'light'
                          ? 'bg-gray-300 dark:bg-gray-600 theme-text-title'
                          : 'bg-gray-100 dark:bg-gray-800 theme-text-body'
                      }`}
                    >
                      Light
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMode('dark'); }}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        mode === 'dark'
                          ? 'bg-gray-300 dark:bg-gray-600 theme-text-title'
                          : 'bg-gray-100 dark:bg-gray-800 theme-text-body'
                      }`}
                    >
                      Dark
                    </button>
                  </div>
                </div>
                
                {/* Accent Color */}
                <div>
                  <p className="text-xs theme-text-muted mb-2">Accent Color</p>
                  <div className="grid grid-cols-5 gap-2">
                    {['gold', 'blue', 'purple', 'green', 'red'].map((scheme) => (
                      <button
                        key={scheme}
                        onClick={(e) => { e.stopPropagation(); handleColorSchemeChange(scheme); }}
                        className={`aspect-square rounded border-2 transition-all ${
                          colorScheme === scheme
                            ? 'border-gray-400 dark:border-gray-500 scale-110'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        style={{ 
                          backgroundColor: 
                            scheme === 'gold' ? '#D4AF37' :
                            scheme === 'blue' ? '#3B82F6' :
                            scheme === 'purple' ? '#8B5CF6' :
                            scheme === 'green' ? '#10B981' :
                            '#EF4444'
                        }}
                        title={scheme.charAt(0).toUpperCase() + scheme.slice(1)}
                      />
                    ))}
                  </div>
                </div>

                {/* Reset Button */}
                <div className="pt-2 border-t theme-border">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorSchemeChange('gold');
                      setTheme('default');
                      setMode('dark');
                      clearTextColorOverrides();
                    }}
                    className="w-full px-3 py-2 rounded-lg text-sm font-medium bg-gray-200 dark:bg-gray-700 theme-text-title hover:opacity-80 transition-opacity"
                  >
                    Reset to Default
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
          style={{ zIndex: 40 }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navigation;

